import config, {Brand} from '../config';
import {OrderLine} from '../transport/apiServiceResponse';

import {logWarning} from './errorLogging';

export type NameSource = {firstName: string; lastName: string};

/**
 * DIS-1245 — resolve the member display name shown on MemberCard surfaces.
 *
 * Priority:
 *   1. API/form name (rewardsStore.firstName/lastName — populated by signUp / signUpStaff)
 *   2. MobX userStore name (full-session users only — empty for lite users)
 *   3. Empty string + logWarning to flag upstream defect (e.g. DIS-1176 lite session gap)
 *
 * Use on every MemberCard render path reachable from lite signup, including:
 *   - frontend/app/screens/rewards/Success.tsx
 *   - frontend/app/screens/account/AccountRewardsScreen/index.tsx
 *
 * Pass plain `NameSource` objects, not full stores, to keep this pure + unit-testable.
 */
export function resolveMemberDisplayName(
  apiName: NameSource,
  storeName: NameSource,
  context: string,
): string {
  const apiComposed = [apiName.firstName, apiName.lastName]
    .map(part => part?.trim() ?? '')
    .filter(Boolean)
    .join(' ');
  if (apiComposed) return apiComposed;

  const storeComposed = [storeName.firstName, storeName.lastName]
    .map(part => part?.trim() ?? '')
    .filter(Boolean)
    .join(' ');
  if (storeComposed) return storeComposed;

  logWarning(
    new Error('Empty member display name on rewards card render'),
    `rewards.resolveMemberDisplayName.${context}`,
  );
  return '';
}

type BrandRewardsCopy = {
  loyaltyProgramName: string;
  menuTitle: string;
  memberCardTitle: string;
  exclusiveOffersTitle: string;
  promotionsTitle: string;
  memberPublicPricingTitle: string;
  memberPrivatePricingTitle: string;
};

const defaultRewardsCopy: BrandRewardsCopy = {
  loyaltyProgramName: 'More Rewards',
  menuTitle: 'Rewards',
  memberCardTitle: 'More Rewards Card',
  exclusiveOffersTitle: 'Exclusive Offers',
  promotionsTitle: 'Promotions',
  memberPublicPricingTitle: 'Member-Only Prices',
  memberPrivatePricingTitle: 'Just For You',
};

const rewardsCopy: Record<Brand, Partial<BrandRewardsCopy>> = {
  [Brand.BOTTLEMART]: {},
  [Brand.SIPNSAVE]: {},
  [Brand.THIRSTY_CAMEL]: {
    loyaltyProgramName: 'Camel Club',
    menuTitle: 'Camel Club',
    memberCardTitle: 'Camel Club Card',
    exclusiveOffersTitle: 'Unique Rewards',
    // promotionsTitle: 'Camel Competitions',
    memberPublicPricingTitle: 'Club Discounts',
    memberPrivatePricingTitle: 'Tailored Offers',
  },
  [Brand.HARRY_BROWN]: {
    loyaltyProgramName: "Harry's Perks",
    menuTitle: 'Rewards',
    memberCardTitle: "Harry's Perks Card",
  },
  [Brand.DEVELOPMENT]: {},
};

/**
 * @deprecated use {@link brandRewardsCopy} instead
 */
export const rewardsMarketingName =
  rewardsCopy[config.BASE_TENANT].loyaltyProgramName ?? 'More Rewards';

// TODO Ideally we move this into the assets folder under some localization solution
export const brandRewardsCopy = {
  ...defaultRewardsCopy,
  ...rewardsCopy[config.BASE_TENANT],
};

/**
 * Utility function to get display rules for an order line (ie. products)
 * This can determine whether a sticker a shown or product displayed in a carousel
 *
 * TODO: Makes sense to handle display logic in middleware,
 * 1. We don't send unused data to the client in the first place.
 * 2. Cleaner to drive display rules from MW without needing to redeploy frontend.
 *
 * This needs more consideration at it'll mean
 * 1. MW will no longer be a transparent MFL API proxy (we will need our own models)
 * 2. It will be a decent chunk of refactoring work and regression testing
 **/
export const getProductDisplayRules = (orderLine: OrderLine) => {
  // `extra_prices: null` is possible at runtime; object-destructure defaults only
  // cover `undefined`, so null would throw on `.find`.
  const extra_prices = Array.isArray(orderLine.extra_prices) ? orderLine.extra_prices : [];

  const isFiniteCents = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);

  const nonMemberPrice = extra_prices.find(
    price =>
      price.price_level_external_reference === 'non_member_price' &&
      isFiniteCents(price.sell_price),
  );

  const publicLoyaltyPrice = extra_prices.find(
    price =>
      price.price_level_external_reference === 'lmg_loyalty' &&
      price.external_sell_type === 'public' &&
      isFiniteCents(price.sell_price),
  );

  const privateLoyaltyPrice = extra_prices.find(
    price =>
      price.price_level_external_reference === 'lmg_loyalty' &&
      price.external_sell_type === 'private' &&
      isFiniteCents(price.sell_price),
  );

  // Require finite sells so `null < 5399` cannot rank a broken loyalty row as "better".
  const isPublicLoyaltyPricingBetter =
    !!publicLoyaltyPrice &&
    !!nonMemberPrice &&
    publicLoyaltyPrice.sell_price < nonMemberPrice.sell_price;
  const isPrivateLoyaltyPricingBetter =
    !!privateLoyaltyPrice &&
    !!nonMemberPrice &&
    privateLoyaltyPrice.sell_price < nonMemberPrice.sell_price;

  return {
    presentAsPublicMemberProduct: isPublicLoyaltyPricingBetter,
    presentAsPrivateMemberProduct: isPrivateLoyaltyPricingBetter,
  };
};
export type ProductDisplayRules = ReturnType<typeof getProductDisplayRules>;

const getSearchTierRank = (line: OrderLine): 0 | 1 | 2 => {
  const rules = getProductDisplayRules(line);
  if (rules.presentAsPublicMemberProduct) return 0;
  if (rules.presentAsPrivateMemberProduct) return 1;
  return 2;
};

/**
 * DIS-1179: Order search results into 3 tiers, alpha asc by name within each tier.
 *   Tier 0 — Member-Only Price products (public loyalty pricing better than non-member)
 *   Tier 1 — Just For You products (private loyalty pricing better than non-member)
 *   Tier 2 — remaining products
 *
 * Pure: returns a new array, does not mutate the input.
 */
export const applyTierSort = (lines: OrderLine[]): OrderLine[] =>
  [...lines].sort((a, b) => {
    const tier = getSearchTierRank(a) - getSearchTierRank(b);
    if (tier !== 0) return tier;
    return a.name.localeCompare(b.name, undefined, {sensitivity: 'base'});
  });
